// Incident Labs page (#/incidents) and the Home card. Read-only views over the catalogue
// (src/incidents/index.js) and incident progress (src/core/incidentProgress.js): tiers, unlock
// rule (related domain complete), "builds toward" exam tags, and the state of each learner's run.
// Nothing here writes progress. A signed-in learner's solved runs are verified by the server
// (src/core/verifiedLabs.js); the first verified solve of each incident earns incident XP once.
import { h } from './h.js';
import { INCIDENTS, INCIDENT_DOMAIN, TIERS, ticketOf } from '../incidents/index.js';
import { featureLabel, unlockLabel, examTags } from './incident-view.js';

const STATE = {
  locked: { label: 'Locked', action: null },
  available: { label: 'Not started', action: 'Start incident' },
  'in-progress': { label: 'In progress', action: 'Resume' },
  complete: { label: 'Resolved', action: 'Review' },
};

/** Per-incident summary for the page and the Home card. */
export function incidentRows(progress) {
  return INCIDENTS.map((inc) => {
    const state = progress.unitState(INCIDENT_DOMAIN, inc.id);
    const rec = progress.unitRecord(INCIDENT_DOMAIN, inc.id);
    return { inc, state, solved: rec.solved || 0, ticket: ticketOf(inc) };
  });
}

function card({ inc, state, solved, ticket }) {
  const m = inc.incident || {};
  const s = STATE[state] || STATE.locked;
  const open = state !== 'locked';
  return h('li', { class: `rz-inc-card panel ${state}`, 'data-testid': `incident-card-${inc.id}`, 'data-state': state },
    h('div', { class: 'rz-inc-head' },
      h('span', { class: `tk-pri p${String(m.severity || 'P2').replace('P', '')}` }, m.severity || 'P2'),
      h('span', { class: 'rz-inc-site muted small' }, m.site || '')),
    h('h3', {}, inc.title),
    ticket ? h('p', { class: 'rz-inc-ticket mono small' }, `TICKET #${ticket.id} · ${ticket.subject}`) : null,
    m.summary ? h('p', { class: 'small' }, m.summary) : null,
    examTags(inc),
    h('div', { class: 'rz-inc-foot' },
      open
        ? h('span', { class: 'rz-inc-state small', 'data-testid': `incident-state-${inc.id}` }, solved ? `${s.label} · solved ${solved}×` : s.label)
        : h('span', { class: 'rz-inc-state small', 'data-testid': `incident-state-${inc.id}` }, `🔒 Unlocks after ${unlockLabel(inc)}`),
      open ? h('a', { class: `btn small ${state === 'complete' ? 'ghost' : 'primary'}`, href: `#/incidents/${inc.id}`, 'data-testid': `incident-open-${inc.id}` }, s.action) : null));
}

/** #/incidents — every incident grouped by tier. */
export function incidentsPage(progress, { serverVerify = false } = {}) {
  const rows = incidentRows(progress);
  const unlocked = rows.filter((r) => r.state !== 'locked').length;
  const tiers = [...new Set(rows.map((r) => r.inc.incident?.tier || 2))].sort();
  return h('main', { class: 'page rz-incidents', 'data-testid': 'incidents-page', 'data-reading': 'article' },
    h('section', { class: 'panel rz-inc-intro' },
      h('div', { class: 'eyebrow' }, 'INCIDENT LABS'),
      h('h1', {}, 'Real tickets. Real troubleshooting.'),
      h('p', { class: 'lede' }, 'Each incident is a realistic ticket on a RouteZero network with one fault picked at random per run. Collect evidence, fix it to the design, and prove it works. Incidents unlock as you finish the domain that teaches the skills, and each one shows which exam it helps you prepare for.'),
      h('div', { class: 'rz-inc-tags' },
        h('span', { class: 'chip pending', 'data-testid': 'incidents-local' }, featureLabel(serverVerify)),
        h('span', { class: 'chip', 'data-testid': 'incidents-count' }, `${unlocked} of ${rows.length} unlocked`))),
    tiers.map((t) => h('section', { class: 'rz-inc-tier', 'data-testid': `incident-tier-${t}` },
      h('div', { class: 'rz-section-head' }, h('div', {},
        h('div', { class: 'eyebrow' }, `TIER ${t}`),
        h('h2', {}, TIERS[t]?.name || `Tier ${t}`)),
        h('span', { class: 'muted small' }, TIERS[t]?.blurb || '')),
      h('ol', { class: 'rz-inc-list' }, rows.filter((r) => (r.inc.incident?.tier || 2) === t).map(card)))));
}

/** Home card: how many incidents are open, and the next one to try. */
export function incidentsHomeCard(progress) {
  const rows = incidentRows(progress);
  const open = rows.filter((r) => r.state !== 'locked');
  const next = open.find((r) => r.state === 'in-progress') || open.find((r) => r.state === 'available') || null;
  const first = rows.find((r) => r.state === 'locked');
  return h('section', { class: 'panel rz-inc-home', 'data-testid': 'home-incidents' },
    h('div', {},
      h('div', { class: 'eyebrow' }, 'INCIDENT LABS · LOCAL PRACTICE'),
      h('h2', {}, 'Practice real network incidents'),
      h('p', { class: 'muted small', 'data-testid': 'home-incidents-text' }, open.length
        ? `${open.length} of ${rows.length} incidents unlocked${next ? ` · next: ticket #${next.ticket?.id} ${next.inc.title}` : ' · all unlocked incidents resolved'}.`
        : `Finish ${first ? unlockLabel(first.inc) : 'a domain'} to unlock your first incidents.`)),
    h('div', { class: 'row' },
      next ? h('a', { class: 'btn primary small', href: `#/incidents/${next.inc.id}`, 'data-testid': 'home-incidents-next' }, next.state === 'in-progress' ? 'Resume incident' : 'Start incident') : null,
      h('a', { class: 'btn ghost small', href: '#/incidents', 'data-testid': 'home-incidents-all' }, 'All Incident Labs →')));
}
